import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

/**
 * RebuildScoreCard — v2 breakdown card. Shows the pillar-by-pillar earned
 * points per the pathway config, plus LIVE / FINAL status.
 */
export function RebuildScoreCard({ score, className = "" }) {
  if (!score || !score.breakdown) return null;
  const status = score.status || "LIVE";
  const total = Math.round(score.display_score || 0);
  const totalOf = 100;
  const pillars = Object.entries(score.breakdown);
  const badge =
    status === "FINAL"
      ? "bg-foreground text-background"
      : "bg-primary/10 text-primary border border-primary/30";
  return (
    <div
      className={`bg-card border border-border rounded-lg p-6 ${className}`}
      data-testid="rebuild-score-card-v2"
    >
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <div className="label-eyebrow">Rebuild Score</div>
          <div className="mt-2 flex items-baseline gap-2">
            <div className="wordmark text-6xl leading-none" data-testid="rebuild-score-total">
              {total}
              <span className="text-2xl text-muted-foreground">/{totalOf}</span>
            </div>
            <div className="font-head uppercase tracking-widest text-xs text-muted-foreground">
              {score.band}
            </div>
          </div>
          <div className="mt-2 text-[11px] uppercase tracking-widest text-muted-foreground">
            Week {score.week} · {score.pathway}
          </div>
        </div>
        <span
          className={`font-head uppercase tracking-widest text-[10px] px-2 py-1 rounded-md ${badge}`}
          data-testid="rebuild-score-status"
        >
          {status === "FINAL" ? "Final Weekly" : "Live · This Week"}
        </span>
      </div>

      <div className="mt-5 space-y-2.5">
        {pillars.map(([name, p]) => {
          const earned = Number(p.earned ?? 0);
          const weight = Number(p.weight ?? 0);
          const successful = p.successful ?? 0;
          const eligible = p.eligible ?? 0;
          const pct = weight ? Math.min(100, (earned / weight) * 100) : 0;
          return (
            <div key={name} data-testid={`rebuild-pillar-${name.toLowerCase()}`}>
              <div className="flex items-baseline justify-between text-xs">
                <span className="font-head uppercase tracking-widest">
                  {name} — {successful}/{eligible || "—"}
                </span>
                <span className="text-muted-foreground">
                  {Math.round(earned)}/{weight}
                </span>
              </div>
              <div className="mt-1 h-1.5 rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full bg-primary transition-[width] duration-500"
                  style={{ width: `${pct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      <p className="mt-5 text-[11px] leading-relaxed text-muted-foreground">
        Your Rebuild Score measures how consistently you're executing your BA30 system. Focus on consistency, not perfection.
      </p>
    </div>
  );
}

/**
 * EatCheckinButton — daily EAT check-in for LEAN / BUILD dashboards.
 * 2-question simple modal that POSTs { status } to `/api/{pathway}/log/eat`.
 */
export function EatCheckinButton({ pathway, onDone, className = "" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [q1, setQ1] = useState(null); // balanced plate today
  const [q2, setQ2] = useState(null); // protein target hit
  const path = pathway === "BUILD" ? "/build/log/eat" : "/lean/log/eat";
  const label = pathway === "BUILD" ? "Log Today's Eat" : "Log Today's Eat";

  const submit = async () => {
    if (q1 === null || q2 === null) {
      toast.error("Answer both questions.");
      return;
    }
    setBusy(true);
    // Both yes = MET, one yes = MOSTLY_MET, none = NOT_MET
    const yesCount = (q1 ? 1 : 0) + (q2 ? 1 : 0);
    const status = yesCount === 2 ? "MET" : yesCount === 1 ? "MOSTLY_MET" : "NOT_MET";
    try {
      await api.post(path, { status });
      toast.success("Eat check-in logged.");
      setOpen(false);
      setQ1(null); setQ2(null);
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`btn-primary btn-sm ${className}`}
        data-testid={`eat-checkin-${pathway.toLowerCase()}-btn`}
      >
        {label}
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => !busy && setOpen(false)}>
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="label-eyebrow">Daily Eat Check-in · {pathway}</div>
            <h3 className="font-head uppercase text-xl mt-2">Two quick questions.</h3>
            {[
              { key: "q1", label: "Did at least 2 of your main meals today follow the BA30 plate method?", get: q1, set: setQ1 },
              { key: "q2", label: pathway === "BUILD" ? "Did you hit your BUILD protein/energy target today?" : "Did you hit your daily protein target today?", get: q2, set: setQ2 },
            ].map(({ key, label, get, set }) => (
              <div key={key} className="mt-4">
                <div className="text-sm">{label}</div>
                <div className="mt-2 flex gap-2">
                  {[["Yes", true], ["No", false]].map(([lbl, v]) => (
                    <button
                      key={lbl}
                      onClick={() => set(v)}
                      className={`px-4 py-2 rounded-md text-sm font-head uppercase tracking-widest border transition-colors ${get === v ? "bg-foreground text-background border-foreground" : "border-border hover:bg-muted"}`}
                      data-testid={`eat-checkin-${key}-${lbl.toLowerCase()}`}
                    >
                      {lbl}
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} disabled={busy} className="text-sm underline text-muted-foreground">Cancel</button>
              <button onClick={submit} disabled={busy} className="btn-primary btn-sm" data-testid="eat-checkin-submit">
                {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
